import { useCallback, useState } from 'react';

import { Weekday } from '@occc/graphql';

import { SYSTEM_WEEKDAYS, WEEKDAYS } from '../touchpoint.constants';

export type CalendarViewType = 'week' | 'day';

type UseScheduleCalendarReturn = {
  calendarView: CalendarViewType;
  selectedDay: Weekday;
  setCalendarView: (view: CalendarViewType) => void;
  goToNextDay: () => void;
  goToPrevDay: () => void;
};

const getNextDay = (currentDay: Weekday): Weekday => {
  const currentIndex = WEEKDAYS.findIndex((d) => d.value === currentDay);
  const nextIndex = (currentIndex + 1) % WEEKDAYS.length;
  return WEEKDAYS[nextIndex].value;
};

const getPrevDay = (currentDay: Weekday): Weekday => {
  const currentIndex = WEEKDAYS.findIndex((d) => d.value === currentDay);
  const prevIndex = (currentIndex - 1 + WEEKDAYS.length) % WEEKDAYS.length;
  return WEEKDAYS[prevIndex].value;
};

/**
 * Hook for managing calendar view state and day navigation.
 * Provides functionality to toggle between week/day views and navigate through days.
 *
 * @returns {UseScheduleCalendarReturn} Calendar state and navigation functions:
 *   - `calendarView` - Current view mode ('week' or 'day')
 *   - `selectedDay` - Currently selected day of the week
 *   - `setCalendarView` - Updates the calendar view mode
 *   - `goToNextDay` - Navigates to the next day (wraps around from Saturday to Sunday)
 *   - `goToPrevDay` - Navigates to the previous day (wraps around from Sunday to Saturday)
 */
export const useScheduleCalendar = (): UseScheduleCalendarReturn => {
  const [calendarView, setCalendarView] = useState<CalendarViewType>('week');
  const [selectedDay, setSelectedDay] = useState<Weekday>(
    () => SYSTEM_WEEKDAYS[new Date().getDay()],
  );

  const goToNextDay = useCallback(() => {
    setSelectedDay((current) => getNextDay(current));
  }, []);

  const goToPrevDay = useCallback(() => {
    setSelectedDay((current) => getPrevDay(current));
  }, []);

  return {
    calendarView,
    selectedDay,
    setCalendarView,
    goToNextDay,
    goToPrevDay,
  };
};
